import { CodeXml, GitBranch, Users, X } from "lucide-react";

const t = {
  fr: {
    title: "Architecture Technique",
    subtitle: "Comprendre le Système EcoLunch",
    conceptTitle: "Concept de Base",
    conceptDesc: "EcoLunch est la plateforme officielle utilisée par les fournisseurs de services de restauration scolaire (« traiteurs ») pour gérer leurs programmes de repas dans les écoles primaires et pour les parents pour passer des commandes.",
    keyPoints: "Points Clés :",
    keyPointsList: [
      "Les traiteurs n'ont PAS leur propre site web séparé",
      "Les parents ne commandent PAS depuis une « plateforme de traiteur »",
      "Les écoles n'interagissent PAS avec plusieurs systèmes",
      "Tout se passe à l'intérieur d'un seul écosystème",
    ],
    conceptBadge: "EcoLunch est la vedette. Les traiteurs opèrent simplement au sein d'EcoLunch.",
    rolesTitle: "Rôles et Relations",
    roles: [
      { title: "EcoLunch", sub: "La Plateforme Centrale", desc: "EcoLunch est le système central pour la gestion des repas, les commandes, les comptes parents, la supervision des écoles, les rapports, la communication et les flux de paiement." },
      { title: "Traiteur", sub: "Fournisseur de Services", desc: "Un traiteur utilise EcoLunch pour configurer les menus, les écoles et les jours de service. Leur logo apparaît, mais EcoLunch reste l'interface principale." },
      { title: "Parent", sub: "Utilisateur Final", desc: "Pour les parents, c'est toujours EcoLunch, quelle que soit l'école primaire, le traiteur ou la région. Un compte pour tout." },
      { title: "École", sub: "Superviseur", desc: "Les écoles accèdent à EcoLunch pour surveiller les services, voir les commandes, consulter les menus et suivre les changements. Elles s'appuient sur EcoLunch pour la visibilité." },
    ],
    flowTitle: "Flux Opérationnel",
    flows: [
      { step: 1, title: "Le traiteur configure son service", desc: "Dans EcoLunch, le traiteur définit les écoles desservies, les dates de service, les menus, les articles, les options, les délais de commande, les prix et les politiques." },
      { step: 2, title: "Les parents voient ce qui s'applique", desc: "Le parent sélectionne l'enfant, l'école et la classe. Ensuite, ils voient le menu et l'horaire du traiteur dans l'interface EcoLunch." },
      { step: 3, title: "Les parents commandent et paient", desc: "Les paiements sont traités dans EcoLunch avec une intégration de passerelle de paiement sécurisée." },
      { step: 4, title: "Le traiteur reçoit les rapports", desc: "EcoLunch génère des rapports de production, des décomptes d'articles, des listes de classe, des résumés quotidiens et des signalements d'allergies." },
      { step: 5, title: "Les écoles supervisent", desc: "Les écoles utilisent EcoLunch pour vérifier les services, voir les commandes, contrôler la qualité et examiner les opérations quotidiennes." },
    ],
    structureTitle: "Structure du Système",
    structureDesc: "Cette plateforme n'est pas un système en marque blanche et n'est pas une architecture multi-locataire de style Shopify.",
    structureBold: "C'est UN SEUL ÉCOSYSTÈME, avec des données et des permissions segmentées.",
    structureSub: "Toutes les entités existent à l'intérieur d'un seul environnement EcoLunch. Les permissions contrôlent l'accès. La marque est unifiée.",
    notTitle: "Ce que EcoLunch N'EST PAS",
    notItems: [
      "Pas un site web de marque de traiteur", "Pas une plateforme en marque blanche",
      "Pas une place de marché", "Pas un Shopify pour les traiteurs",
      "Pas une architecture « instance séparée par traiteur »", "Pas invisible derrière la marque du traiteur",
    ],
    notConclusion: "EcoLunch est la plateforme que tout le monde voit et reconnaît.",
  },
  en: {
    title: "Technical Architecture",
    subtitle: "Understanding the EcoLunch System",
    conceptTitle: "Core Concept",
    conceptDesc: "EcoLunch is the official platform used by school meal service providers (\"caterers\") to manage their meal programs in elementary schools and for parents to place orders.",
    keyPoints: "Key Points:",
    keyPointsList: [
      "Caterers do NOT have their own separate website",
      "Parents do NOT order from a \"caterer platform\"",
      "Schools do NOT interact with multiple systems",
      "Everything happens within a single ecosystem",
    ],
    conceptBadge: "EcoLunch is the star. Caterers simply operate within EcoLunch.",
    rolesTitle: "Roles and Relationships",
    roles: [
      { title: "EcoLunch", sub: "The Central Platform", desc: "EcoLunch is the central system for meal management, orders, parent accounts, school oversight, reports, communication and payment flows." },
      { title: "Caterer", sub: "Service Provider", desc: "A caterer uses EcoLunch to configure menus, schools and service days. Their logo appears, but EcoLunch remains the main interface." },
      { title: "Parent", sub: "End User", desc: "For parents, it's always EcoLunch, regardless of the school, caterer or region. One account for everything." },
      { title: "School", sub: "Supervisor", desc: "Schools access EcoLunch to monitor services, view orders, check menus and track changes." },
    ],
    flowTitle: "Operational Flow",
    flows: [
      { step: 1, title: "Caterer configures service", desc: "In EcoLunch, the caterer defines schools served, service dates, menus, items, options, order deadlines, prices and policies." },
      { step: 2, title: "Parents see what applies", desc: "The parent selects child, school and class. Then they see the caterer's menu and schedule in the EcoLunch interface." },
      { step: 3, title: "Parents order and pay", desc: "Payments are processed in EcoLunch with secure payment gateway integration." },
      { step: 4, title: "Caterer receives reports", desc: "EcoLunch generates production reports, item counts, class lists, daily summaries and allergy alerts." },
      { step: 5, title: "Schools oversee", desc: "Schools use EcoLunch to verify services, view orders, control quality and review daily operations." },
    ],
    structureTitle: "System Structure",
    structureDesc: "This platform is not a white-label system and not a Shopify-style multi-tenant architecture.",
    structureBold: "It's ONE SINGLE ECOSYSTEM, with segmented data and permissions.",
    structureSub: "All entities exist within a single EcoLunch environment. Permissions control access. Branding is unified.",
    notTitle: "What EcoLunch is NOT",
    notItems: [
      "Not a caterer brand website", "Not a white-label platform",
      "Not a marketplace", "Not a Shopify for caterers",
      "Not a \"separate instance per caterer\" architecture", "Not invisible behind the caterer's brand",
    ],
    notConclusion: "EcoLunch is the platform everyone sees and recognizes.",
  },
};

export default function ArchitectureSection({ lang }) {
  const c = t[lang];
  return (
    <section data-testid="architecture-section" className="py-16 sm:py-24 bg-darker">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-primary/20 mb-6">
            <CodeXml className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">{c.title}</h2>
          <p className="text-xl text-white/70">{c.subtitle}</p>
        </div>
        <div className="max-w-5xl mx-auto mb-16">
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30">
            <div className="p-6 py-8">
              <h3 className="text-2xl font-bold text-white mb-4">{c.conceptTitle}</h3>
              <p className="text-lg text-white/80 mb-6">{c.conceptDesc}</p>
              <p className="text-lg font-bold text-primary mb-4">{c.keyPoints}</p>
              <ul className="space-y-3 mb-6">
                {c.keyPointsList.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0 mt-2 glow-primary" />
                    <span className="text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="inline-flex items-center rounded-md border border-transparent shadow bg-primary text-primary-foreground text-base px-4 py-2 font-bold">{c.conceptBadge}</div>
            </div>
          </div>
        </div>
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-white text-center mb-12">{c.rolesTitle}</h3>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {c.roles.map((role) => (
              <div key={role.title} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-primary/20">
                <div className="p-6 pt-6 pb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center glow-primary">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="text-lg font-bold text-primary">{role.title}</h4>
                      <p className="text-xs text-white/60">{role.sub}</p>
                    </div>
                  </div>
                  <p className="text-sm text-white/80">{role.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-white text-center mb-12">{c.flowTitle}</h3>
          <div className="max-w-4xl mx-auto space-y-6">
            {c.flows.map((flow) => (
              <div key={flow.step} className="rounded-xl border text-card-foreground shadow hover-lift bg-dark border-l-4 border-l-primary">
                <div className="p-6 py-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-black glow-primary">{flow.step}</div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-white mb-2">{flow.title}</h4>
                      <p className="text-sm text-white/70">{flow.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mb-16 max-w-4xl mx-auto">
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-primary/30">
            <div className="p-6 py-8">
              <div className="flex items-center gap-3 mb-6">
                <GitBranch className="h-8 w-8 text-primary" />
                <h3 className="text-2xl font-bold text-white">{c.structureTitle}</h3>
              </div>
              <p className="text-white/80 mb-4">{c.structureDesc}</p>
              <p className="text-lg font-bold text-primary mb-4">{c.structureBold}</p>
              <p className="text-white/70">{c.structureSub}</p>
            </div>
          </div>
        </div>
        <div className="max-w-5xl mx-auto">
          <div className="rounded-xl border text-card-foreground shadow bg-dark border-destructive/30">
            <div className="p-6 py-8">
              <div className="flex items-center gap-3 mb-6">
                <X className="h-8 w-8 text-destructive" />
                <h3 className="text-2xl font-bold text-white">{c.notTitle}</h3>
              </div>
              <ul className="grid sm:grid-cols-2 gap-4 mb-6">
                {c.notItems.map((item) => (
                  <li key={item} className="flex items-start gap-2">
                    <X className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-white/70">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-primary/10 rounded-lg p-4 border border-primary/30">
                <p className="text-lg font-bold text-primary text-center">{c.notConclusion}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
